import React, { useState, useEffect, ChangeEvent } from 'react'
import {
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    TextField,
    InputAdornment,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Chip,
    Box,
    FormHelperText,
    Input,
    Paper,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import ClearIcon from '@material-ui/icons/Clear'

const useStyles = makeStyles((theme) => ({
    searchFieldContainer: {
        padding: theme.spacing(1, 2),
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchField: {
        width: '100%',
    },
    dropdownContainer: {
        maxHeight: 450,
        display: 'flex',
        flexDirection: 'column',
    },
    optionsContainer: {
        overflow: 'auto',
        flex: 1,
        maxHeight: 370, // Increased from 220 to show more items
    },
    paginationControls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
    },
    paginationButton: {
        minWidth: 'unset',
        padding: theme.spacing(0.5),
    },
    paginationInfo: {
        fontSize: '0.875rem',
    },
    selectedChip: {
        margin: theme.spacing(0.5),
    },
    selectedChipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: theme.spacing(1),
    },
    noResultsItem: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        textAlign: 'center',
    },
}))

// Constants for pagination
const ITEMS_PER_PAGE = 25

export interface SelectOption {
    id: number
    name: string
    subtext?: string
}

interface PaginatedSearchSelectProps {
    options: SelectOption[]
    value: number | number[] | null
    onChange: (value: any) => void
    label: string
    placeholder?: string
    multiple?: boolean
    helperText?: string
    disabled?: boolean
    renderCustomSubtext?: (option: SelectOption) => string | null
    clearable?: boolean
}

const PaginatedSearchSelect: React.FC<PaginatedSearchSelectProps> = ({
    options,
    value,
    onChange,
    label,
    placeholder = 'Search...',
    multiple = false,
    helperText,
    disabled = false,
    renderCustomSubtext,
    clearable = false,
}) => {
    const classes = useStyles()

    // State
    const [searchTerm, setSearchTerm] = useState('')
    const [currentPage, setCurrentPage] = useState(0)
    const [dropdownOpen, setDropdownOpen] = useState(false)

    // Handle empty or null values
    const normalizedValue = React.useMemo(() => {
        if (value === null || value === undefined) {
            return multiple ? [] : ''
        }
        return value
    }, [value, multiple])

    // Filter options by search term
    const filteredOptions = options.filter((option) =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    // Calculate pagination
    const totalPages = Math.ceil(filteredOptions.length / ITEMS_PER_PAGE)
    const paginatedOptions = filteredOptions.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE,
    )

    // Reset pagination when search or options change
    useEffect(() => {
        setCurrentPage(0)
    }, [searchTerm, options])

    // Validation effect to ensure valid selections
    useEffect(() => {
        if (value !== null && value !== undefined) {
            if (multiple && Array.isArray(value)) {
                // Ensure all selected IDs exist in options
                const validIds = value.filter((id) =>
                    options.some((opt) => opt.id === id),
                )
                if (validIds.length !== value.length) {
                    onChange(validIds)
                }
            } else if (!multiple && typeof value === 'number') {
                // Ensure the selected ID exists in options
                const isValid = options.some((opt) => opt.id === value)
                if (!isValid && options.length > 0) {
                    onChange(null)
                }
            }
        }
    }, [options, value, multiple, onChange])

    const handleNextPage = () => {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
    }

    const handlePrevPage = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 0))
    }

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    }

    const handleDropdownOpen = () => {
        setDropdownOpen(true)
    }

    const handleDropdownClose = () => {
        setDropdownOpen(false)
        setSearchTerm('')
        setCurrentPage(0)
    }

    const getOptionById = (id: number) => {
        return options.find((option) => option.id === id)
    }

    const handleRemoveOption = (optionId: number, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation()
        }
        if (multiple && Array.isArray(value)) {
            onChange(value.filter((id) => id !== optionId))
        }
    }

    const handleSelectItem = (optionId: number, event: React.MouseEvent) => {
        event.stopPropagation()

        if (multiple && Array.isArray(value)) {
            // Toggle selection for multiple mode
            const newValue = value.includes(optionId)
                ? value.filter((id) => id !== optionId)
                : [...value, optionId]
            onChange(newValue)
        } else {
            // Toggle selection for single mode and close dropdown
            onChange(value === optionId ? null : optionId)
            if (!multiple) {
                // Close dropdown when selecting in single mode
                handleDropdownClose()
            }
        }
    }

    const renderSelectedOptionChips = () => {
        if (!multiple || !Array.isArray(value) || value.length === 0)
            return null

        return (
            <div className={classes.selectedChipsContainer}>
                <Typography variant="body2" color="textSecondary">
                    Selected items:
                </Typography>
                <Box display="flex" flexWrap="wrap" mt={0.5}>
                    {value.map((id) => {
                        const option = getOptionById(id)
                        return option ? (
                            <Chip
                                key={id}
                                label={option.name}
                                onDelete={(e) => handleRemoveOption(id, e)}
                                size="small"
                                className={classes.selectedChip}
                            />
                        ) : null
                    })}
                </Box>
            </div>
        )
    }

    const renderNoResults = () => (
        <Typography className={classes.noResultsItem}>
            {filteredOptions.length === 0
                ? 'No items match your search'
                : 'No items available for selection'}
        </Typography>
    )

    const isItemSelected = (id: number): boolean => {
        if (multiple && Array.isArray(value)) {
            return value.includes(id)
        }
        return value === id
    }

    return (
        <FormControl fullWidth>
            <InputLabel
                id={`${label.replace(/\s+/g, '-').toLowerCase()}-select-label`}
            >
                {label}
            </InputLabel>
            <Select
                labelId={`${label
                    .replace(/\s+/g, '-')
                    .toLowerCase()}-select-label`}
                multiple={multiple}
                value={normalizedValue}
                onChange={(e) => onChange(e.target.value)}
                input={
                    <Input
                        endAdornment={
                            clearable &&
                            value !== null &&
                            (Array.isArray(value)
                                ? value.length > 0
                                : value !== undefined) ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="clear selection"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onChange(multiple ? [] : null)
                                        }}
                                        size="small"
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                    />
                }
                onOpen={handleDropdownOpen}
                onClose={handleDropdownClose}
                open={dropdownOpen}
                renderValue={(selected) => {
                    if (multiple && Array.isArray(selected)) {
                        const count = selected.length
                        return count > 0
                            ? `${count} ${
                                  count === 1 ? 'item' : 'items'
                              } selected`
                            : placeholder
                    } else if (selected === null || selected === '') {
                        return placeholder
                    } else {
                        const selectedOption = getOptionById(selected as number)
                        return selectedOption
                            ? selectedOption.name
                            : placeholder
                    }
                }}
                disabled={disabled}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: 400,
                        },
                    },
                }}
            >
                <div className={classes.dropdownContainer}>
                    <div className={classes.searchFieldContainer}>
                        <TextField
                            className={classes.searchField}
                            placeholder={placeholder}
                            value={searchTerm}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    <div className={classes.optionsContainer}>
                        {filteredOptions.length === 0 ||
                        paginatedOptions.length === 0
                            ? renderNoResults()
                            : paginatedOptions.map((option) => (
                                  <MenuItem
                                      key={option.id}
                                      value={option.id}
                                      onClick={(e) =>
                                          handleSelectItem(option.id, e)
                                      }
                                  >
                                      {multiple && (
                                          <Checkbox
                                              checked={isItemSelected(
                                                  option.id,
                                              )}
                                              onClick={(e) => {
                                                  e.stopPropagation()
                                                  handleSelectItem(option.id, e)
                                              }}
                                          />
                                      )}
                                      <ListItemText
                                          primary={option.name}
                                          secondary={
                                              renderCustomSubtext
                                                  ? renderCustomSubtext(option)
                                                  : option.subtext
                                          }
                                      />
                                  </MenuItem>
                              ))}
                    </div>

                    {filteredOptions.length > 0 && (
                        <div className={classes.paginationControls}>
                            <IconButton
                                className={classes.paginationButton}
                                disabled={currentPage === 0}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handlePrevPage()
                                }}
                                size="small"
                            >
                                <KeyboardArrowUpIcon />
                            </IconButton>
                            <Typography className={classes.paginationInfo}>
                                {`Page ${currentPage + 1} of ${totalPages}`}
                            </Typography>
                            <IconButton
                                className={classes.paginationButton}
                                disabled={currentPage >= totalPages - 1}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleNextPage()
                                }}
                                size="small"
                            >
                                <KeyboardArrowDownIcon />
                            </IconButton>
                        </div>
                    )}
                </div>
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
            {renderSelectedOptionChips()}
        </FormControl>
    )
}

export default PaginatedSearchSelect
